// v2/components/SavingsCard.js
// B2B kazanci + cariye tanimli aktif indirimler
// Kaynak: /api/v2/dashboard/ savings + activeDiscounts
import React from 'react';
import { ResponsiveContainer, BarChart, Bar, Tooltip, XAxis } from 'recharts';
import { Pin, Tag, ArrowRight, RotateCw, Clock } from 'lucide-react';
import { formatTRY, formatPercent, formatNumber } from '../utils/format';
import { tarihTR } from '../utils/mapAccount';
import stat from './StatCards.module.css';
import styles from './SavingsCard.module.css';

// 'ELEKTRIK' -> 'Elektrik' (tr-TR lower kullanilmiyor: I -> ı olur)
const grupAdi = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '');

function Oranlar({ rates }) {
  return (
    <span className={styles.chips}>
      {rates.map((r, k) => (
        <React.Fragment key={k}>
          {k > 0 && <span className={styles.plus}>+</span>}
          <span className={styles.chip}>%{String(r).replace('.', ',')}</span>
        </React.Fragment>
      ))}
    </span>
  );
}

function Bitis({ x }) {
  if (x.openEnded || !x.endsAt) return <span className={styles.end}>Süresiz</span>;
  if (x.endingSoon) {
    const metin = x.daysLeft !== null && x.daysLeft <= 0 ? 'Bugün bitiyor' : `Son ${x.daysLeft} gün`;
    return (
      <span className={`${styles.end} ${styles.endSoon}`}>
        <Clock size={11} />{metin}
      </span>
    );
  }
  return <span className={styles.end}>Bitiş: {tarihTR(x.endsAt)}</span>;
}

function SparkTip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className={styles.tip}>
      <div className={styles.tipLabel}>{label}</div>
      <div>{formatTRY(payload[0].value)}</div>
    </div>
  );
}

export default function SavingsCard({
  savings, discounts, loading = false, error = null, onRetry, onShowCampaigns = () => {},
}) {
  const bos = !savings && !discounts;

  return (
    <section className={stat.card} aria-label="Kazancım ve İndirimlerim">
      <div className={stat.cardHead}>
        <h2 className={stat.cardTitle}>Kazancım ve İndirimlerim</h2>
        {discounts && discounts.customerCount > 0 && (
          <span className={styles.badge}>
            <Pin size={11} />{discounts.customerCount} indirimli
          </span>
        )}
      </div>

      {bos ? (
        <div className={stat.cardState}>
          {loading ? 'Yükleniyor…' : (
            <>
              <span>{error || 'İndirim bilgileri alınamadı.'}</span>
              {onRetry && (
                <button type="button" className={stat.retryBtn} onClick={onRetry}>
                  <RotateCw size={12} /> Tekrar dene
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          {savings && (
            <div className={styles.top}>
              <div>
                <p className={stat.accCellLabel}>B2B siparişlerinizdeki kazancınız ({savings.year})</p>
                <div className={styles.amount}>{formatTRY(savings.amount)}</div>
                <div className={styles.sub}>
                  {savings.grossTotal > 0
                    ? <>Brüt fiyata göre ortalama <b>{formatPercent(savings.avgRate, 1)}</b> indirim</>
                    : 'Bu yıl B2B siparişiniz bulunmuyor.'}
                </div>
              </div>
              {savings.trend.some((t) => t.v > 0) && (
                <div className={styles.spark} aria-label="Son 6 ay kazanç">
                  <div className={styles.sparkChart}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={savings.trend} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                        <XAxis dataKey="m" hide />
                        <Tooltip content={<SparkTip />} cursor={{ fill: 'rgba(22,163,106,.08)' }} />
                        <Bar dataKey="v" fill="#16A36A" radius={[3, 3, 0, 0]} maxBarSize={12}
                             isAnimationActive animationDuration={620} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <span className={styles.sparkLabel}>Son 6 ay</span>
                </div>
              )}
            </div>
          )}

          {discounts && (
            <div className={styles.listWrap}>
              <h3 className={styles.listTitle}>Aktif İndirimleriniz</h3>
              {discounts.items.length === 0 ? (
                <p className={styles.empty}>Aktif indirim veya kampanya bulunmuyor.</p>
              ) : (
                <ul className={styles.list}>
                  {discounts.items.map((x) => (
                    <li key={x.id} className={styles.row}>
                      <span
                        className={`${styles.kind} ${x.kind === 'customer' ? styles.kindCustomer : styles.kindGeneral}`}
                        title={x.kind === 'customer' ? 'İndirimli' : 'Kampanyalı'}
                      >
                        {x.kind === 'customer' ? <Pin size={11} /> : <Tag size={11} />}
                      </span>
                      <span className={styles.target}>
                        <span className={styles.targetName} title={x.name || undefined}>
                          {x.scope === 'group'
                            ? `${grupAdi(x.target)} grubu`
                            : (x.targetName || x.target)}
                        </span>
                        <span className={styles.meta}>
                          {x.kind === 'customer' ? 'İndirimli' : 'Kampanyalı'}
                          {' · '}
                          <Bitis x={x} />
                        </span>
                      </span>
                      <Oranlar rates={x.rates} />
                      <span className={styles.eff} title="Zincir sırayla uygulandığında gerçek indirim">
                        {formatPercent(x.effective, 1)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {discounts.hasMore && (
                <p className={styles.note}>İlk {discounts.items.length} indirim gösteriliyor.</p>
              )}
              {discounts.items.some((x) => x.rates.length > 1) && (
                <p className={styles.note}>
                  Zincir indirimler toplanmaz, sırayla uygulanır. Sağdaki oran gerçek indirimdir.
                </p>
              )}
            </div>
          )}

          {discounts && discounts.generalProducts.count > 0 && (
            <button type="button" className={styles.promo} onClick={onShowCampaigns}>
              <Tag size={14} />
              <span className={styles.promoText}>
                <b>{formatNumber(discounts.generalProducts.count)} kampanyalı ürün</b>
                {discounts.generalProducts.maxRate > 0 && (
                  <> · en yüksek {formatPercent(discounts.generalProducts.maxRate, 0)} indirim</>
                )}
              </span>
              <ArrowRight size={14} />
            </button>
          )}
        </>
      )}
    </section>
  );
}
